import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import OnboardingPageHeader from '../ComponentOnboarding/OnboardingPageHeader';
import { AddRepositoryBackButton } from './AddRepositoryPickPanel';
import { useAppSelector } from '@/state/store';
import { usePostInstrumentation } from '@/components/smartai/api/postInstrumentation';
import { usePostGithubInstrumentation } from '@/components/smartai/api/postGithubInstrumentation';
import { formatEntryPoints } from '@/utils/findEntryPointFiles';
import { cn } from '@/lib/utils';
const normalizeRepoUrl = (url: string) =>
  url
    .trim()
    .replace(/\.git$/i, '')
    .replace(/\/+$/, '')
    .toLowerCase();

type ResultCardProps = {
  title: string;
  ready?: boolean;
  rows: Array<{ label: string; value: string; valueClassName?: string }>;
};

function ResultCard({ title, ready = true, rows }: ResultCardProps) {
  return (
    <div className="overflow-hidden rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]">
      <div className="flex items-start justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border-soft)] px-5 py-4">
        <p className="m-0 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]">
          {title}
        </p>
        {ready ? (
          <span className="shrink-0 rounded-full border border-solid border-[#abefc6] bg-[#ecfdf3] px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-[#027a48]">
            Ready
          </span>
        ) : null}
      </div>
      <dl className="m-0 divide-y divide-solid divide-[var(--inv-border-soft)]">
        {rows.map(row => (
          <div
            key={row.label}
            className="grid grid-cols-1 gap-1 border-0 px-5 py-3.5 sm:grid-cols-[minmax(7.5rem,34%)_1fr] sm:items-start sm:gap-6">
            <dt className="m-0 text-[0.8125rem] leading-[1.45] text-[var(--inv-text-muted)]">
              {row.label}
            </dt>
            <dd
              className={cn(
                'm-0 text-[0.8125rem] font-medium leading-[1.45] text-[var(--inv-text)] sm:text-right',
                row.valueClassName,
              )}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

type AddRepositoryResultsPanelProps = {
  elapsedTime: string;
  onBack: () => void;
  onFinish: () => void;
};

const AddRepositoryResultsPanel = ({
  elapsedTime,
  onBack,
  onFinish,
}: AddRepositoryResultsPanelProps) => {
  const {
    scmProvider,
    scanResult,
    scanOnly,
    org,
    azureProject,
    repoName,
    testOrg,
    testProject,
    testPipelineName,
    testRepoUrl,
    testBranchName,
    testPipelinePlatform,
    patToken,
    testPatToken,
    prEmails,
    autoPrEmails,
    repoUrl,
    branchName,
    pipelineName,
    repoId,
    isFrontend,
  } = useAppSelector(state => state.onboarding);

  const [serviceUrl, setServiceUrl] = useState('');
  const instrumentationStarted = useRef(false);
  const isGithub = scmProvider === 'github';

  const mainAdoMutation = usePostInstrumentation(patToken);
  const testAdoMutation = usePostInstrumentation(testPatToken);
  const mainGithubMutation = usePostGithubInstrumentation(patToken);
  const testGithubMutation = usePostGithubInstrumentation(testPatToken);

  useEffect(() => {
    if (scanOnly || !patToken || instrumentationStarted.current) return;
    instrumentationStarted.current = true;

    const reviewers = prEmails.length ? prEmails : autoPrEmails;
    const sameTestRepo =
      !!testRepoUrl?.trim() &&
      normalizeRepoUrl(testRepoUrl) === normalizeRepoUrl(repoUrl);

    if (isGithub) {
      mainGithubMutation.mutate({
        github_org: org,
        repo_url: repoUrl,
        target_branch: branchName.replace(/^refs\/heads\//, ''),
        reviewers,
        repo_id: repoId || undefined,
        instrumentation_target: 'app',
      });
      if (testRepoUrl?.trim() && testPatToken && !sameTestRepo) {
        testGithubMutation.mutate({
          github_org: testOrg || org,
          repo_url: testRepoUrl,
          target_branch: (testBranchName || branchName).replace(
            /^refs\/heads\//,
            '',
          ),
          reviewers: autoPrEmails?.length ? autoPrEmails : reviewers,
          repo_id: repoId || undefined,
          instrumentation_target: 'test',
        });
      }
      return;
    }

    mainAdoMutation.mutate({
      azure_org: org,
      azure_project: azureProject,
      pipeline_name: pipelineName,
      repo_url: repoUrl,
      target_branch: branchName.replace(/^refs\/heads\//, ''),
      reviewers,
      backend_project: '',
      repo_id: repoId || undefined,
      instrumentation_target: 'app',
    });

    if (testRepoUrl?.trim() && !sameTestRepo) {
      testAdoMutation.mutate({
        azure_org: testOrg,
        azure_project: testProject,
        pipeline_name: testPipelineName,
        repo_url: testRepoUrl,
        target_branch: testBranchName.replace(/^refs\/heads\//, ''),
        reviewers: autoPrEmails,
        backend_project: azureProject,
        repo_id: repoId || undefined,
        instrumentation_target: 'test',
      });
    }
    // Trigger once when results panel mounts after a scan-and-instrument flow.
  }, [scanOnly, patToken]);

  const ast = scanResult?.astAnalysis;
  const totalLines = ast?.totalLines ?? 0;
  const executable = ast?.totalExecutableLines ?? 0;
  const executablePct =
    totalLines > 0 ? `${Math.round((executable / totalLines) * 100)}%` : '—';

  const entryPointsLabel = useMemo(() => {
    const fileDetails =
      (ast?.fileDetails as
        | Array<{ filePath?: string; fileName?: string }>
        | undefined) ?? [];
    const paths = fileDetails.map(
      file => file.filePath || file.fileName || '',
    );
    return formatEntryPoints(paths);
  }, [ast?.fileDetails]);

  const appTitle = repoName || org;
  const testTitle = isGithub
    ? testOrg || 'Test automation'
    : testProject || testOrg || 'Test automation';

  const frameworkLabel =
    testPipelinePlatform === 'jenkins'
      ? 'Jenkins'
      : testPipelinePlatform === 'github-actions'
        ? 'GitHub Actions'
        : testPipelinePlatform === 'ado'
          ? 'Azure Pipelines'
          : '—';

  const footerNote = scanOnly
    ? `Scanned in ${elapsedTime} · nothing will be written to either repository`
    : `Scanned and instrumented in ${elapsedTime} · pull requests will be raised when ready`;

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--inv-bg)]">
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-8 pt-8 max-[520px]:px-5">
        <AddRepositoryBackButton onBack={onBack} />
        <OnboardingPageHeader
          eyebrow="Scan complete"
          title={appTitle}
        />

        <section className="mt-6">
          <h3 className="m-0 text-[1rem] font-semibold text-[var(--inv-text)]">
            What we found
          </h3>
          <p className="mt-1 mb-4 text-[0.8125rem] text-[var(--inv-text-muted)]">
            All of this was read from the repositories.
          </p>

          <div className="space-y-4">
            <ResultCard
              title={`Application · ${appTitle.toUpperCase()}`}
              rows={[
                {
                  label: 'Project',
                  value: isGithub ? organizationLabel(org) : azureProject || '—',
                },
                {
                  label: 'Files · lines',
                  value: `${ast?.totalFiles ?? 0} · ${totalLines.toLocaleString()}`,
                },
                {
                  label: 'Executable',
                  value: `${executable.toLocaleString()} (${executablePct})`,
                },
                {
                  label: 'Kind',
                  value: isFrontend ? 'Frontend' : 'Backend / mixed',
                },
                {
                  label: 'Entry points',
                  value: entryPointsLabel,
                  valueClassName:
                    'font-mono text-[0.75rem] leading-[1.5] sm:text-right',
                },
              ]}
            />

            {testRepoUrl ? (
              <ResultCard
                title={`Tests · ${testTitle.toUpperCase()}`}
                rows={[
                  // { label: 'Framework', value: frameworkLabel },
                  {
                    label: 'Pipeline',
                    value: testPipelineName || '—',
                  },
                  {
                    label: 'Repository',
                    value: testRepoUrl.split('/').pop() ?? testRepoUrl,
                  },
                ]}
              />
            ) : null}
          </div>
        </section>
      </div>

      <div className="shrink-0 border-t border-solid border-[var(--inv-border)] bg-[var(--inv-bg)] px-8 py-4 max-[520px]:px-5">
        <div className="flex items-center justify-between gap-4">
          <p className="m-0 text-[0.8125rem] leading-[1.5] text-[var(--inv-text-muted)]">
            {footerNote}
          </p>
          <Button type="button" className="h-10 shrink-0" onClick={onFinish}>
            Add repository
          </Button>
        </div>
      </div>
    </div>
  );
};

function organizationLabel(org: string) {
  return org || '—';
}

export default AddRepositoryResultsPanel;
